import { createModuleLogger } from '../utils/logger';
import type { Attendee, LocationInfo, MeetingInfo } from '../types/meeting';

const log = createModuleLogger('MeetingOfficeService');

function getUserDomain(): string {
  const email = Office.context.mailbox?.userProfile?.emailAddress ?? '';
  const parts = email.split('@');
  const domain = parts.length === 2 ? parts[1].toLowerCase() : '';
  log.debug({ hasEmail: email.length > 0 }, 'User domain extracted');
  return domain;
}

function classifyAttendee(
  emailAddress: { displayName?: string; emailAddress?: string } | undefined,
  internalDomain: string,
): Attendee {
  const email = (emailAddress?.emailAddress ?? '').toLowerCase();
  const name = emailAddress?.displayName ?? '';
  const attendeeDomain = email.split('@')[1] ?? '';
  const isInternal = attendeeDomain === internalDomain;

  log.debug({ type: isInternal ? 'internal' : 'external' }, 'Classifying attendee');

  return {
    name: name || email,
    email,
    type: isInternal ? 'internal' : 'external',
  };
}

/**
 * Deduplicates attendees by email address, keeping the first occurrence.
 * Useful when attendees appear in both required and optional lists.
 */
function deduplicateAttendees(attendees: Attendee[]): Attendee[] {
  const seen = new Set<string>();
  const unique: Attendee[] = [];

  for (const attendee of attendees) {
    if (!seen.has(attendee.email)) {
      seen.add(attendee.email);
      unique.push(attendee);
    } else {
      log.debug('Deduplicating attendee');
    }
  }

  if (attendees.length !== unique.length) {
    log.info(
      {
        original: attendees.length,
        deduplicated: unique.length,
        removed: attendees.length - unique.length,
      },
      'Attendee deduplication completed',
    );
  }

  return unique;
}

function detectLocation(item: Office.AppointmentRead): Promise<LocationInfo> {
  return new Promise((resolve) => {
    const supportsEnhanced = Office.context.requirements.isSetSupported('Mailbox', '1.8');
    log.debug({ supported: supportsEnhanced }, 'Feature detection - Enhanced Location support');

    if (supportsEnhanced && item.enhancedLocation) {
      log.debug('Using enhanced location detection (Office 1.8+)');
      item.enhancedLocation.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value?.length > 0) {
          const locations: Array<{
            displayName: string;
            locationIdentifier?: { type?: string };
          }> = result.value;
          const hasRoom = locations.some((loc) => loc.locationIdentifier?.type === 'room');
          const displayName = locations.map((l) => l.displayName).join(', ');
          log.info(
            {
              isRoom: hasRoom,
              count: locations.length,
            },
            'Enhanced location detected',
          );
          resolve({ displayName, isRoom: hasRoom });
        } else {
          log.debug('Enhanced location call failed or returned empty, falling back');
          fallbackLocation(item, resolve);
        }
      });
    } else {
      log.debug('Enhanced location not supported, using fallback');
      fallbackLocation(item, resolve);
    }
  });
}

function fallbackLocation(
  item: Office.AppointmentRead,
  resolve: (info: LocationInfo) => void,
): void {
  const loc = item.location as unknown as string;
  if (loc && loc.trim().length > 0) {
    const onlinePatterns = /teams|zoom|webex|online|virtual|https?:\/\//i;
    const isRoom = !onlinePatterns.test(loc);
    log.debug({ hasLocation: true, isRoom }, 'Fallback location detected');
    resolve({ displayName: loc, isRoom });
  } else {
    log.debug('No location found');
    resolve({ displayName: '', isRoom: false });
  }
}

export async function readMeetingFromItem(): Promise<MeetingInfo | null> {
  log.info('Starting meeting read');
  const item = Office.context.mailbox?.item;
  if (!item) {
    log.warn('No item available in mailbox');
    return null;
  }

  const internalDomain = getUserDomain();
  const userEmail = (Office.context.mailbox.userProfile?.emailAddress ?? '').toLowerCase();

  const isComposeMode =
    item.itemType === Office.MailboxEnums.ItemType.Appointment &&
    typeof (item as unknown as Record<string, { getAsync?: unknown }>).subject?.getAsync ===
      'function';

  log.debug({ isComposeMode }, 'Mode detection');

  if (isComposeMode) {
    log.info('Reading from compose mode');
    return readFromCompose(item as unknown as Office.AppointmentCompose, internalDomain, userEmail);
  }

  log.info('Reading from read mode');
  const appt = item as unknown as Office.AppointmentRead;
  const subject: string = (appt.subject as unknown as string) ?? '';
  const start: Date | null = appt.start ? new Date(appt.start as unknown as string) : null;
  const end: Date | null = appt.end ? new Date(appt.end as unknown as string) : null;

  const timeZone =
    Office.context.mailbox.userProfile?.timeZone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const organizer = appt.organizer
    ? {
        name: appt.organizer.displayName ?? '',
        email: (appt.organizer.emailAddress ?? '').toLowerCase(),
      }
    : { name: '', email: '' };

  const isOrganizer = organizer.email === userEmail;
  log.debug({ isOrganizer }, 'Organizer check');

  const allAttendees: Attendee[] = [];
  if (appt.requiredAttendees) {
    for (const a of appt.requiredAttendees as unknown as Array<{
      displayName?: string;
      emailAddress?: string;
    }>) {
      allAttendees.push(classifyAttendee(a, internalDomain));
    }
  }
  if (appt.optionalAttendees) {
    for (const a of appt.optionalAttendees as unknown as Array<{
      displayName?: string;
      emailAddress?: string;
    }>) {
      allAttendees.push(classifyAttendee(a, internalDomain));
    }
  }

  const deduplicatedAttendees = deduplicateAttendees(allAttendees);
  const internalAttendees = deduplicatedAttendees.filter((a) => a.type === 'internal');
  const externalAttendees = deduplicatedAttendees.filter((a) => a.type === 'external');

  log.info(
    {
      total: deduplicatedAttendees.length,
      internal: internalAttendees.length,
      external: externalAttendees.length,
    },
    'Attendees processed',
  );

  const location = await detectLocation(appt);
  const isInPerson = location.isRoom;
  const canOrderCatering = isOrganizer && isInPerson;
  const isPastMeeting = end ? end < new Date() : false;

  log.info(
    {
      isOrganizer,
      isInPerson,
      canOrderCatering,
    },
    'Catering eligibility determined',
  );

  const result = {
    subject,
    start,
    end,
    timeZone,
    organizer,
    location,
    attendees: deduplicatedAttendees,
    internalAttendees,
    externalAttendees,
    isOrganizer,
    isInPerson,
    canOrderCatering,
    isPastMeeting,
  };

  log.info({ canOrderCatering }, 'Meeting read completed successfully');

  return result;
}

export function subscribeToMeetingChanges(onChange: () => void): () => void {
  if (typeof Office === 'undefined') return () => undefined;

  const mailbox = Office.context.mailbox;
  const item = mailbox?.item as (Office.AppointmentRead & Office.AppointmentCompose) | undefined;
  if (!mailbox || !item?.addHandlerAsync) return () => undefined;

  const handler = () => onChange();
  const itemEvents = [
    Office.EventType.AppointmentTimeChanged,
    Office.EventType.EnhancedLocationsChanged,
    Office.EventType.RecipientsChanged,
  ].filter((eventType): eventType is Office.EventType => Boolean(eventType));

  for (const eventType of itemEvents) {
    item.addHandlerAsync(eventType, handler, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        log.warn({ eventType }, 'Meeting change subscription unavailable');
      }
    });
  }

  mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler, (result) => {
    if (result.status !== Office.AsyncResultStatus.Succeeded) {
      log.warn('Meeting item change subscription unavailable');
    }
  });

  return () => {
    for (const eventType of itemEvents) {
      item.removeHandlerAsync(eventType, handler);
    }
    mailbox.removeHandlerAsync(Office.EventType.ItemChanged, handler);
  };
}

function getAsync<T>(obj: {
  getAsync: (cb: (result: Office.AsyncResult<T>) => void) => void;
}): Promise<T | null> {
  return new Promise((resolve) => {
    obj.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        resolve(null);
      }
    });
  });
}

async function readFromCompose(
  appt: Office.AppointmentCompose,
  internalDomain: string,
  userEmail: string,
): Promise<MeetingInfo> {
  log.info('Reading from compose mode');
  const subject = (await getAsync<string>(appt.subject)) ?? '';
  const start = (await getAsync<Date>(appt.start)) ?? null;
  const end = (await getAsync<Date>(appt.end)) ?? null;

  const timeZone =
    (Office.context.mailbox.userProfile as unknown as { timeZone?: string })?.timeZone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const organizer = {
    name: Office.context.mailbox.userProfile?.displayName ?? '',
    email: userEmail,
  };
  const isOrganizer = true;
  log.debug('Compose mode - user is organizer');

  const allAttendees: Attendee[] = [];
  const required = await getAsync<Office.EmailAddressDetails[]>(appt.requiredAttendees);
  if (required) {
    for (const a of required) {
      allAttendees.push(classifyAttendee(a, internalDomain));
    }
  }
  const optional = await getAsync<Office.EmailAddressDetails[]>(appt.optionalAttendees);
  if (optional) {
    for (const a of optional) {
      allAttendees.push(classifyAttendee(a, internalDomain));
    }
  }

  const deduplicatedAttendees = deduplicateAttendees(allAttendees);
  const internalAttendees = deduplicatedAttendees.filter((a) => a.type === 'internal');
  const externalAttendees = deduplicatedAttendees.filter((a) => a.type === 'external');

  log.info(
    {
      total: deduplicatedAttendees.length,
      internal: internalAttendees.length,
      external: externalAttendees.length,
    },
    'Compose attendees processed',
  );

  const location = await detectLocationCompose(appt);
  const isInPerson = location.isRoom;
  const canOrderCatering = isOrganizer && isInPerson;

  log.info(
    {
      isOrganizer,
      isInPerson,
      canOrderCatering,
    },
    'Compose catering eligibility determined',
  );

  return {
    subject,
    start,
    end,
    timeZone,
    organizer,
    location,
    attendees: deduplicatedAttendees,
    internalAttendees,
    externalAttendees,
    isOrganizer,
    isInPerson,
    canOrderCatering,
    isPastMeeting: end ? end < new Date() : false,
  };
}

function detectLocationCompose(appt: Office.AppointmentCompose): Promise<LocationInfo> {
  return new Promise((resolve) => {
    const supportsEnhanced = Office.context.requirements.isSetSupported('Mailbox', '1.8');
    log.debug(
      { supported: supportsEnhanced },
      'Compose - Feature detection - Enhanced Location support',
    );

    if (supportsEnhanced && appt.enhancedLocation) {
      log.debug('Compose - Using enhanced location detection');
      appt.enhancedLocation.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value?.length > 0) {
          const locations = result.value as Array<{
            displayName: string;
            locationIdentifier?: { type?: string };
          }>;
          const hasRoom = locations.some((loc) => loc.locationIdentifier?.type === 'room');
          const displayName = locations.map((l) => l.displayName).join(', ');
          log.info(
            {
              isRoom: hasRoom,
              count: locations.length,
            },
            'Compose - Enhanced location detected',
          );
          resolve({ displayName, isRoom: hasRoom });
        } else {
          log.debug('Compose - Enhanced location call failed, falling back');
          fallbackLocationCompose(appt, resolve);
        }
      });
    } else {
      log.debug('Compose - Enhanced location not supported, using fallback');
      fallbackLocationCompose(appt, resolve);
    }
  });
}

function fallbackLocationCompose(
  appt: Office.AppointmentCompose,
  resolve: (info: LocationInfo) => void,
): void {
  appt.location.getAsync((result) => {
    if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
      const loc = result.value;
      const onlinePatterns = /teams|zoom|webex|online|virtual|https?:\/\//i;
      const isRoom = !onlinePatterns.test(loc);
      log.debug({ hasLocation: true, isRoom }, 'Compose - Fallback location detected');
      resolve({ displayName: loc, isRoom });
    } else {
      log.debug('Compose - No location found');
      resolve({ displayName: '', isRoom: false });
    }
  });
}
